import { api, ErroreApi } from './api.ts'
import { inAppNativa, sistemaTelefono } from './app-nativa.ts'

/**
 * Telefoni che restano connessi (521, decisioni di Davide dell'1/11/2026).
 *
 * Nell'app Android il telefono si ricorda da solo dopo l'accesso, e alla sessione scaduta l'app ne apre
 * una nuova senza chiedere niente: lo sblocco del telefono lo chiede l'app stessa. Nella web app (il
 * gestionale aggiunto alla schermata Home dell'iPhone) il telefono si ricorda solo se la persona attiva
 * Face ID: la ripresa chiede ogni volta Face ID o il codice dell'iPhone (WebAuthn, passkey del telefono).
 *
 * Nel browser di un computer (531, decisioni di Davide del 2/11/2026) la persona puo' scegliere «Ricordami
 * su questo computer»: la stessa passkey, della piattaforma (Windows Hello, Touch ID), e la pagina di
 * accesso offre «Entra con Windows Hello» o «con Touch ID» accanto alla password. Le sessioni di quel
 * computer restano aperte 4 ore senza attivita'.
 */

export type ModoTelefono = 'app' | 'schermata_home'

export function modoTelefono(): ModoTelefono | null {
  if (inAppNativa()) return 'app'
  const daHome = window.matchMedia?.('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true
  return daHome ? 'schermata_home' : null
}

/**
 * Un browser di computer: non l'app, non la web app dell'iPhone, non un telefono o un tablet. Solo qui si
 * propone «Ricordami su questo computer».
 */
export function suComputer(): boolean {
  return modoTelefono() === null && sistemaTelefono() === 'altro'
}

/** Come si chiama la verifica della persona su questo computer, per i pulsanti. */
export function nomeVerifica(): string {
  const ua = navigator.userAgent
  if (/Windows/.test(ua)) return 'Windows Hello'
  if (/Macintosh|Mac OS X/.test(ua)) return 'Touch ID'
  return 'la verifica del computer'
}

/** Il browser puo' usare una passkey della piattaforma: Face ID, Touch ID, Windows Hello, il codice del telefono. */
export async function passkeyDisponibile(): Promise<boolean> {
  try {
    return !!window.PublicKeyCredential
      && await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

const daB64url = (s: string) => {
  const b = atob(s.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(s.length / 4) * 4, '='))
  return Uint8Array.from(b, (c) => c.charCodeAt(0))
}
const inB64url = (b: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(b))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

type OpzioniRegistrazione = {
  challenge: string; rp: { id: string; name: string }; user: { id: string; name: string; displayName: string }
  pubKeyCredParams: { type: 'public-key'; alg: number }[]
  authenticatorSelection: AuthenticatorSelectionCriteria; attestation: AttestationConveyancePreference; timeout: number
}
export type OpzioniSblocco = {
  challenge: string; rpId: string; allowCredentials: { type: 'public-key'; id: string }[]
  userVerification: UserVerificationRequirement; timeout: number
}

/** App: apre una sessione dal telefono ricordato. Falso se il telefono non e' ricordato. */
export async function riprendiApp(): Promise<boolean> {
  try {
    await api.post('/auth/telefono/riprendi', {})
    return true
  } catch (e) {
    if (e instanceof ErroreApi && (e.stato === 401 || e.stato === 429)) return false
    throw e
  }
}

/** App: ricorda il telefono dopo un accesso completo. Un errore non deve fermare niente. */
export async function ricordaApp(): Promise<void> {
  try { await api.post('/auth/telefono/ricorda', { tipo: 'app' }) } catch { /* si riprova al prossimo accesso */ }
}

/** Web app: la sfida per lo sblocco, se questo telefono e' ricordato con la sua passkey. */
export async function sfidaSblocco(): Promise<OpzioniSblocco | null> {
  try {
    return (await api.post<{ opzioni: OpzioniSblocco }>('/auth/telefono/sfida', {})).opzioni
  } catch (e) {
    if (e instanceof ErroreApi && (e.stato === 401 || e.stato === 409 || e.stato === 429)) return null
    throw e
  }
}

/**
 * Web app: Face ID (o il codice) sulla sfida gia' scaricata, poi la ripresa. Va chiamata dal tocco
 * della persona, senza attese prima: Safari vuole il gesto per aprire Face ID.
 */
export async function sblocca(o: OpzioniSblocco): Promise<void> {
  const credenziale = await navigator.credentials.get({
    publicKey: {
      challenge: daB64url(o.challenge), rpId: o.rpId, timeout: o.timeout, userVerification: o.userVerification,
      allowCredentials: o.allowCredentials.map((c) => ({ type: c.type, id: daB64url(c.id) })),
    },
  }) as PublicKeyCredential | null
  if (!credenziale) throw new Error('Sblocco annullato.')
  const r = credenziale.response as AuthenticatorAssertionResponse
  await api.post('/auth/telefono/riprendi', {
    clientDataJSON: inB64url(r.clientDataJSON),
    authenticatorData: inB64url(r.authenticatorData),
    signature: inB64url(r.signature),
  })
}

/**
 * Primo passo di «Resta connesso» (web app) o di «Ricordami su questo computer»: le opzioni della passkey,
 * da preparare prima del tocco.
 */
export async function preparaPasskey(tipo: 'schermata_home' | 'computer' = 'schermata_home'): Promise<OpzioniRegistrazione> {
  return (await api.post<{ opzioni: OpzioniRegistrazione }>('/auth/telefono/ricorda', { tipo })).opzioni
}

/** Web app, secondo passo: Face ID crea la passkey, il gestionale la registra. Dal tocco della persona. */
export async function registraPasskey(o: OpzioniRegistrazione): Promise<void> {
  const credenziale = await navigator.credentials.create({
    publicKey: {
      challenge: daB64url(o.challenge), rp: o.rp,
      user: { id: daB64url(o.user.id), name: o.user.name, displayName: o.user.displayName },
      pubKeyCredParams: o.pubKeyCredParams, authenticatorSelection: o.authenticatorSelection,
      attestation: o.attestation, timeout: o.timeout,
    },
  }) as PublicKeyCredential | null
  if (!credenziale) throw new Error('Registrazione annullata.')
  const r = credenziale.response as AuthenticatorAttestationResponse
  await api.post('/auth/telefono/passkey', {
    clientDataJSON: inB64url(r.clientDataJSON),
    attestationObject: inB64url(r.attestationObject),
  })
}

// --- computer ricordato (531) -------------------------------------------------------------------------

/**
 * Il cookie del computer ricordato e' httpOnly e va solo a /api/auth: la pagina non lo vede. Per non chiedere
 * la sfida a ogni pagina di accesso di ogni computer (le rotte di accesso hanno un limite stretto per
 * indirizzo, e i colleghi di una sede escono dallo stesso), il browser tiene questo segnale: nessun segreto,
 * solo «qui c'e' un computer ricordato». Se il server dice di no, si toglie.
 */
const SEGNALE_COMPUTER = 'cidis.computer-ricordato'

export function computerRicordatoQui(): boolean {
  try { return window.localStorage.getItem(SEGNALE_COMPUTER) === '1' } catch { return false }
}

export function segnaComputerRicordato(ricordato: boolean) {
  try {
    if (ricordato) window.localStorage.setItem(SEGNALE_COMPUTER, '1')
    else window.localStorage.removeItem(SEGNALE_COMPUTER)
  } catch { /* archivio del browser vietato: la pagina di accesso offre solo la password */ }
}

/** La sfida del computer ricordato, con il nome di chi l'ha ricordato. Null se il computer non lo e' (piu'). */
export async function sfidaComputer(): Promise<{ opzioni: OpzioniSblocco; nome: string } | null> {
  try {
    const r = await api.post<{ opzioni: OpzioniSblocco; tipo: string; nome?: string }>('/auth/telefono/sfida', {})
    if (r.tipo !== 'computer') return null
    return { opzioni: r.opzioni, nome: r.nome ?? '' }
  } catch (e) {
    if (e instanceof ErroreApi && e.stato === 401) { segnaComputerRicordato(false); return null }
    if (e instanceof ErroreApi && e.stato === 429) return null
    throw e
  }
}

/** «Non ora» alla proposta: per 30 giorni, per utenza, in questo browser (i computer delle sedi sono condivisi). */
const chiaveRimando = (utente: string) => `cidis.ricorda-computer-rimandato.${utente}`

export function propostaComputerRimandata(utente: string): boolean {
  try { return Number(window.localStorage.getItem(chiaveRimando(utente))) > Date.now() } catch { return false }
}

export function rimandaPropostaComputer(utente: string, giorni = 30) {
  try {
    window.localStorage.setItem(chiaveRimando(utente), String(Date.now() + giorni * 24 * 60 * 60_000))
  } catch { /* resta chiusa fino al ricaricamento */ }
}

/** Nome leggibile del dispositivo dall'etichetta del server (`chrome/windows`, audit.ts): «Chrome su Windows». */
export function nomeDispositivo(etichetta: string | null): string | null {
  if (!etichetta) return null
  const [browser, sistema] = etichetta.split('/')
  const b = ({ chrome: 'Chrome', edge: 'Edge', firefox: 'Firefox', safari: 'Safari' } as Record<string, string>)[browser]
  const s = ({ windows: 'Windows', macos: 'Mac', ios: 'iPhone', android: 'Android' } as Record<string, string>)[sistema]
  if (b && s) return `${b} su ${s}`
  return b ?? s ?? null
}
